import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useCreateOffer } from "@/hooks/use-offers";
import { useAdvertisers } from "@/hooks/use-advertisers";
import { useOfferGroups } from "@/hooks/use-offer-groups";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { MacroPicker } from "@/components/MacroPicker";

export function OfferCreatePage() {
  const navigate = useNavigate();
  const createMutation = useCreateOffer();
  const { data: advData } = useAdvertisers({ pageSize: 200 });
  const advertisers = advData?.data ?? [];
  const { data: groupData } = useOfferGroups({ pageSize: 200 });
  const offerGroups = groupData?.data ?? [];

  const [form, setForm] = useState({
    name: "",
    advertiserId: "",
    offerGroupId: "",
    destinationUrl: "",
    previewUrl: "",
    status: "active",
    visibility: "public",
    currencyId: "USD",
    description: "",
    redirectMode: "standard",
    conversionMethod: "server_postback",
    sessionDurationHours: "24",
    isCapsEnabled: false,
    dailyConversionCap: "0",
    dailyClickCap: "0",
  });

  const [error, setError] = useState("");

  function updateField(field: string, value: string | boolean) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        advertiserId: Number(form.advertiserId),
        offerGroupId: form.offerGroupId ? Number(form.offerGroupId) : null,
        destinationUrl: form.destinationUrl,
        previewUrl: form.previewUrl || "",
        status: form.status,
        visibility: form.visibility,
        currencyId: form.currencyId,
        description: form.description,
        redirectMode: form.redirectMode,
        conversionMethod: form.conversionMethod,
        sessionDurationHours: Number(form.sessionDurationHours),
        isCapsEnabled: form.isCapsEnabled,
        dailyConversionCap: Number(form.dailyConversionCap),
        dailyClickCap: Number(form.dailyClickCap),
      };

      const result = await createMutation.mutateAsync(payload);
      navigate({ to: "/offers/$id", params: { id: String(result.data.id) } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create offer");
    }
  }

  return (
    <div>
      <PageHeader title="Create Offer" description="Add a new offer to your catalog" backHref="/offers" />

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
        {error && (
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        <Card title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Offer Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="e.g., Premium VPN Subscription"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser *</label>
              <select
                required
                value={form.advertiserId}
                onChange={(e) => updateField("advertiserId", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Select advertiser...</option>
                {advertisers.map((a: { id: number; name: string }) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Offer Group</label>
              <select
                value={form.offerGroupId}
                onChange={(e) => updateField("offerGroupId", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">None</option>
                {offerGroups.map((g: { id: number; name: string }) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => updateField("status", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
              <select
                value={form.visibility}
                onChange={(e) => updateField("visibility", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="public">Public</option>
                <option value="require_approval">Require Approval</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={form.currencyId}
                onChange={(e) => updateField("currencyId", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </Card>

        <Card title="URLs">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Destination URL *</label>
                <MacroPicker onInsert={(macro) => updateField("destinationUrl", form.destinationUrl + macro)} />
              </div>
              <input
                type="text"
                required
                value={form.destinationUrl}
                onChange={(e) => updateField("destinationUrl", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="https://advertiser.com/landing?tid={transaction_id}"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preview URL</label>
              <input
                type="url"
                value={form.previewUrl}
                onChange={(e) => updateField("previewUrl", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="https://advertiser.com/preview"
              />
            </div>
          </div>
        </Card>

        <Card title="Tracking Configuration">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Redirect Mode</label>
              <select
                value={form.redirectMode}
                onChange={(e) => updateField("redirectMode", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="standard">Standard</option>
                <option value="meta_refresh">Meta Refresh</option>
                <option value="double_meta_refresh">Double Meta Refresh</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conversion Method</label>
              <select
                value={form.conversionMethod}
                onChange={(e) => updateField("conversionMethod", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="server_postback">Server Postback</option>
                <option value="pixel">Pixel</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session Duration (hours)</label>
              <input
                type="number"
                min="1"
                max="8760"
                value={form.sessionDurationHours}
                onChange={(e) => updateField("sessionDurationHours", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>
        </Card>

        <Card title="Description">
          <textarea
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder="Describe the offer..."
          />
        </Card>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: "/offers" })}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? "Creating..." : "Create Offer"}
          </button>
        </div>
      </form>
    </div>
  );
}
